import type { CSSProperties } from 'react';

type Dot = {
  x: number;
  y: number;
  radius: number;
  opacity: number;
  phase: number;
  isWhite: boolean;
  pulseX: number;
  pulseY: number;
  pulseOffset: number;
};

const noise = (index: number, salt: number) => {
  const value = Math.sin((index + 1) * 12.9898 + salt * 78.233) * 43758.5453;
  return value - Math.floor(value);
};

// The mark is a rounded chevron — two straight legs of constant thickness that
// splay outward from a filleted peak — plus a free-standing counter dot. The
// numbers below are measured off assets/logo.png, rescaled to the 0-100
// viewBox, so the dot field traces the real letterform rather than an
// approximation of it.
const A_CENTER_X = 49.8;
// Perpendicular half-thickness of the stroke, shared by the legs and the peak.
const A_HALF_STROKE = 4.97;
// The peak is a circular fillet joining the two leg centrelines. A_SIN_SPLAY is
// the sine of the legs' angle from vertical and doubles as the angular cutoff
// that limits the fillet to the arc actually drawn.
const A_ARC_CENTER_Y = 30.81;
const A_ARC_RADIUS = 5.86;
const A_SIN_SPLAY = 0.3916;
// Where each leg centreline leaves the fillet, and where it ends (round cap).
const A_TANGENT_X = 5.38;
const A_TANGENT_Y = 28.52;
const A_LEG_END_X = 24.0;
const A_LEG_END_Y = 72.5;
const A_DOT_CENTER_Y = 68.5;
const A_DOT_RADIUS = 8.22;

const distanceToSegment = (x: number, y: number, ax: number, ay: number, bx: number, by: number) => {
  const dx = bx - ax;
  const dy = by - ay;
  const t = Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / (dx * dx + dy * dy)));
  return Math.hypot(x - (ax + t * dx), y - (ay + t * dy));
};

const isInsideA = (x: number, y: number) => {
  const arcDistance = Math.hypot(x - A_CENTER_X, y - A_ARC_CENTER_Y);
  const peak = A_ARC_CENTER_Y - y >= A_SIN_SPLAY * arcDistance
    && Math.abs(arcDistance - A_ARC_RADIUS) <= A_HALF_STROKE;
  const leftLeg = distanceToSegment(
    x, y,
    A_CENTER_X - A_TANGENT_X, A_TANGENT_Y,
    A_CENTER_X - A_LEG_END_X, A_LEG_END_Y,
  ) <= A_HALF_STROKE;
  const rightLeg = distanceToSegment(
    x, y,
    A_CENTER_X + A_TANGENT_X, A_TANGENT_Y,
    A_CENTER_X + A_LEG_END_X, A_LEG_END_Y,
  ) <= A_HALF_STROKE;
  const dot = Math.hypot(x - A_CENTER_X, y - A_DOT_CENTER_Y) <= A_DOT_RADIUS;

  return peak || leftLeg || rightLeg || dot;
};

const isNearA = (x: number, y: number) => (
  isInsideA(x - 2.8, y)
  || isInsideA(x + 2.8, y)
  || isInsideA(x, y - 2.8)
  || isInsideA(x, y + 2.8)
);

const CLOUD_CENTER = 50;
const CLOUD_RADIUS = 46.5;
// How far a rim dot travels outward at full strength, in viewBox units.
const PULSE_TRAVEL = 2.6;
// Share of the pulse period spread between the centre and the rim.
const PULSE_WAVE_SPAN = 0.62;
const PULSE_JITTER = 0.08;

const createPackedDots = (count: number): Dot[] => {
  const points: Array<{ x: number; y: number; nearestDistance: number }> = [];

  for (let index = 0; index < count; index += 1) {
    let bestCandidate = { x: 50, y: 50, nearestDistance: 0 };

    for (let candidateIndex = 0; candidateIndex < 12; candidateIndex += 1) {
      const seed = index * 12 + candidateIndex;
      const angle = noise(seed, 1) * Math.PI * 2;
      const edgeVariation = 0.96 + noise(seed, 2) * 0.08;
      const distance = Math.sqrt(noise(seed, 3)) * CLOUD_RADIUS * edgeVariation;
      const candidate = {
        x: CLOUD_CENTER + Math.cos(angle) * distance,
        y: CLOUD_CENTER + Math.sin(angle) * distance * 0.985,
      };
      const nearestDistance = points.reduce((nearest, point) => {
        const pointDistance = Math.hypot(candidate.x - point.x, candidate.y - point.y);
        return Math.min(nearest, pointDistance);
      }, Number.POSITIVE_INFINITY);

      if (nearestDistance > bestCandidate.nearestDistance) {
        bestCandidate = { ...candidate, nearestDistance };
      }
    }

    points.push(bestCandidate);
  }

  return points.map((point, index) => {
    const nearestDistance = points.reduce((nearest, otherPoint, otherIndex) => {
      if (index === otherIndex) return nearest;
      return Math.min(nearest, Math.hypot(point.x - otherPoint.x, point.y - otherPoint.y));
    }, Number.POSITIVE_INFINITY);
    const isWhite = isInsideA(point.x, point.y);
    const nearA = !isWhite && isNearA(point.x, point.y);
    const sizeRoll = noise(index, 4);
    const desiredRadius = isWhite
      ? 0.78 + noise(index, 5) * 0.6
      : sizeRoll < 0.55
        ? 0.42 + noise(index, 5) * 0.35
        : sizeRoll < 0.85
          ? 0.78 + noise(index, 5) * 0.42
          : 1.2 + noise(index, 5) * 0.55;
    // The letter reads more clearly when its dots sit at full strength and the
    // blue dots hugging its edges step back a little.
    const opacity = isWhite
      ? 0.94 + noise(index, 6) * 0.06
      : (0.56 + noise(index, 6) * 0.42) * (nearA ? 0.74 : 1);
    // Every dot moves straight out from the centre by an amount proportional to
    // how far out it already sits, so the cloud dilates as one body instead of
    // each dot wandering off on its own heading.
    const offsetX = (point.x - CLOUD_CENTER) / CLOUD_RADIUS;
    const offsetY = (point.y - CLOUD_CENTER) / CLOUD_RADIUS;
    // Inner dots run ahead of outer ones, which turns the dilation into a
    // ripple travelling outward. A little jitter keeps the front from looking
    // mechanically perfect.
    const normalisedRadius = Math.min(1, Math.hypot(offsetX, offsetY));
    const jitter = (noise(index, 11) - 0.5) * PULSE_JITTER;
    const pulseOffset = Math.max(0, (1 - normalisedRadius) * PULSE_WAVE_SPAN + jitter);

    return {
      x: point.x,
      y: point.y,
      radius: Math.min(desiredRadius, Math.max(0.34, nearestDistance * (isWhite ? 0.49 : 0.43))),
      opacity,
      phase: noise(index, 7),
      isWhite,
      pulseX: offsetX * PULSE_TRAVEL,
      pulseY: offsetY * PULSE_TRAVEL,
      pulseOffset,
    };
  });
};

const BOTTOM_DOT_FILL: Dot[] = ([
  [49.8, 67.3],
  [47.2, 68.9],
  [52.4, 68.9],
  [49.8, 70.8],
] as Array<[number, number]>).map(([x, y], index) => {
  const offsetX = (x - CLOUD_CENTER) / CLOUD_RADIUS;
  const offsetY = (y - CLOUD_CENTER) / CLOUD_RADIUS;
  const normalisedRadius = Math.min(1, Math.hypot(offsetX, offsetY));

  return {
    x,
    y,
    radius: 0.94 + noise(index, 15) * 0.2,
    opacity: 0.98,
    phase: noise(index, 16),
    isWhite: true,
    pulseX: offsetX * PULSE_TRAVEL,
    pulseY: offsetY * PULSE_TRAVEL,
    pulseOffset: Math.max(0, (1 - normalisedRadius) * PULSE_WAVE_SPAN),
  };
});

const CLOUD_DOTS = [...createPackedDots(640), ...BOTTOM_DOT_FILL];

// Ambient covers idle, connecting and listening; active is the model
// responding. Only the amplitudes change between the two, so the animations
// never restart and the switch can be ramped instead of snapped.
const AMBIENT_MOTION = { pulse: 0.45, shimmer: 0 };
const ACTIVE_MOTION = { pulse: 1, shimmer: 1 };
// Every dot shares the pulse period — that is what makes the ripple coherent.
// Only the delay varies, by radius. The shimmer stays per-dot and unsynced.
const PULSE_DURATION = 3.4;
const SHIMMER_DURATION = 1.4;
const SHIMMER_SPREAD = 1;

export type VoiceLogoProps = {
  amplitude: number;
  intensity?: number;
  isConnected?: boolean;
  /** Upper bound in px. The logo fills its container below that, staying square. */
  size?: number;
  className?: string;
};

type DotStyle = CSSProperties & Record<'--dot-opacity' | '--dx' | '--dy', string | number>;
type RootStyle = CSSProperties & Record<'--ally-pulse' | '--ally-shimmer', number>;

export function VoiceLogo({ amplitude, intensity = 1, isConnected = false, size = 180, className }: VoiceLogoProps) {
  const activeAmplitude = Math.max(0, Math.min(1, amplitude)) * intensity;
  const motion = !isConnected ? { pulse: 0, shimmer: 0 } : activeAmplitude > 0.5 ? ACTIVE_MOTION : AMBIENT_MOTION;

  const rootStyle = {
    position: 'relative',
    width: '100%',
    maxWidth: size,
    aspectRatio: '1',
    display: 'grid',
    placeItems: 'center',
    transition: '--ally-pulse 900ms ease, --ally-shimmer 900ms ease',
    '--ally-pulse': motion.pulse,
    '--ally-shimmer': motion.shimmer,
  } as RootStyle;

  return (
    <div role="img" aria-label="Ally voice activity" className={className} style={rootStyle}>
      <style>{`
        @property --ally-pulse { syntax: '<number>'; inherits: true; initial-value: 0.45; }
        @property --ally-shimmer { syntax: '<number>'; inherits: true; initial-value: 0; }
        @keyframes ally-dot-pulse {
          0%, 100% {
            translate: calc(var(--dx) * -1 * var(--ally-pulse)) calc(var(--dy) * -1 * var(--ally-pulse));
            scale: calc(1 - .05 * var(--ally-pulse));
          }
          50% {
            translate: calc(var(--dx) * var(--ally-pulse)) calc(var(--dy) * var(--ally-pulse));
            scale: calc(1 + .07 * var(--ally-pulse));
          }
        }
        @keyframes ally-dot-shimmer {
          0%, 100% { opacity: var(--dot-opacity); }
          50% { opacity: calc(var(--dot-opacity) * (1 - .5 * var(--ally-shimmer))); }
        }
        @media (prefers-reduced-motion: reduce) {
          .ally-dot-motion { animation: none !important; }
        }
      `}</style>
      <div aria-hidden="true" style={{ position: 'absolute', width: '103%', height: '103%', borderRadius: '50%', background: 'radial-gradient(circle, rgba(74, 164, 255, 0.17), rgba(28, 88, 170, 0.06) 58%, transparent 75%)', filter: 'blur(18px)', opacity: 0.56 + activeAmplitude * 0.18 }} />
      <svg aria-hidden="true" viewBox="0 0 100 100" style={{ overflow: 'visible', position: 'relative', width: '100%', height: '100%' }}>
        {CLOUD_DOTS.map((dot, index) => {
          const style = {
            '--dot-opacity': dot.opacity,
            '--dx': `${dot.pulseX.toFixed(3)}px`,
            '--dy': `${dot.pulseY.toFixed(3)}px`,
            transformBox: 'fill-box',
            transformOrigin: 'center',
            animation: isConnected ? [
              `ally-dot-pulse ${PULSE_DURATION}s ease-in-out ${(dot.pulseOffset * -PULSE_DURATION).toFixed(3)}s infinite`,
              `ally-dot-shimmer ${(SHIMMER_DURATION + dot.phase * SHIMMER_SPREAD).toFixed(2)}s ease-in-out ${(dot.phase * -SHIMMER_DURATION).toFixed(2)}s infinite`,
            ].join(', ') : 'none',
          } as DotStyle;

          return (
            <circle
              key={index}
              className="ally-dot-motion"
              cx={dot.x}
              cy={dot.y}
              r={dot.radius}
              fill={dot.isWhite ? '#ffffff' : '#3b9fff'}
              opacity={dot.opacity}
              style={style}
            />
          );
        })}
      </svg>
    </div>
  );
}

export default VoiceLogo;
